import React, {useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useMutation} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {changePassword} from '@/entities/account/api';
import {useAuthStore} from '@/entities/session/auth-store';
import {ApiError} from '@/shared/api';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {AppInput} from '@/shared/ui/input';
import {Logo} from '@/shared/ui/logo';
import {Screen} from '@/shared/ui/screen';
import {AppText} from '@/shared/ui/text';
import {spacing, useTheme} from '@/shared/ui/theme';

/**
 * The one screen an account flagged `must_change_password` can reach.
 *
 * ## Why it is a whole stack of its own
 *
 * A platform administrator who resets a password mails it in plain text, and the
 * tokens issued next to that flag are ordinary working tokens — the gateway refuses
 * nothing with them. The restriction exists ONLY here, so it cannot be a banner on the
 * dashboard or a row in settings that the user scrolls past: it is the entire tree
 * (`app/navigation.tsx` mounts this and nothing else while the status holds), which is
 * the same trick that keeps onboarding from lingering.
 *
 * ## Why there is no "later" and no sign-out shortcut
 *
 * "Later" would leave a mailed password valid indefinitely, which is the exact failure
 * the flag was added to prevent. Signing out is still possible — it is the button at
 * the bottom — because trapping someone who opened the wrong account would be worse
 * than making them sign in again; but it drops the session rather than passing it.
 *
 * ## Why `change-password` and never `set-password`
 *
 * The account demonstrably HAS a password: it was just mailed one, and that mail is
 * what goes in the "current" field. `set-password` exists for accounts that never had
 * one (social sign-in) and would let this screen skip proving possession of the mail —
 * which is the only proof this flow has that the right person is holding it.
 */
const styles = StyleSheet.create({
  body: {
    gap: spacing(4),
    paddingTop: spacing(8),
    paddingBottom: spacing(10),
    alignItems: 'stretch',
  },
  head: {gap: spacing(2), alignItems: 'center'},
  form: {gap: spacing(3)},
  foot: {alignItems: 'center', paddingTop: spacing(2)},
});

/** The server's own floor. Stated here so the message arrives before the round trip. */
const MIN_PASSWORD = 8;

export function ForcePasswordScreen() {
  const {t} = useTranslation();
  const p = useTheme();
  const passwordChanged = useAuthStore(s => s.passwordChanged);
  const signOut = useAuthStore(s => s.signOut);

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');

  const mismatch = repeat.length > 0 && next !== repeat;
  const tooShort = next.length > 0 && next.length < MIN_PASSWORD;
  const reused = current.length > 0 && next.length > 0 && current === next;
  const canSubmit =
    current.length > 0 &&
    next.length >= MIN_PASSWORD &&
    next === repeat &&
    !reused;

  const submit = useMutation({
    mutationFn: () => changePassword(current, next),
    // 200 IS the server clearing the flag. Lowering the gate before that — on mount,
    // on a skip, on anything else — is what the contract forbids, not this.
    onSuccess: () => passwordChanged(),
  });

  return (
    <Screen
      width="form"
      scroll
      edgeTop={false}
      style={styles.body}
      testID="force-password-screen">
      <View style={styles.head}>
        <Logo width={150} />
        <AppText variant="title" color={p.foreground}>
          {t('forcePassword.title')}
        </AppText>
        <AppText variant="body" color={p.mutedForeground}>
          {t('forcePassword.subtitle')}
        </AppText>
      </View>

      <Card padding={4} style={styles.form}>
        <AppInput
          label={t('forcePassword.mailedPassword')}
          value={current}
          onChangeText={setCurrent}
          secure
          hint={t('forcePassword.mailedHint')}
          showPasswordLabel={t('a11y.showPassword')}
          hidePasswordLabel={t('a11y.hidePassword')}
          testID="force-password-current"
        />
        <AppInput
          label={t('security.newPassword')}
          value={next}
          onChangeText={setNext}
          secure
          hint={t('security.minLength', {count: MIN_PASSWORD})}
          error={
            tooShort
              ? t('security.tooShort', {count: MIN_PASSWORD})
              : reused
                ? t('forcePassword.sameAsMailed')
                : undefined
          }
          showPasswordLabel={t('a11y.showPassword')}
          hidePasswordLabel={t('a11y.hidePassword')}
          testID="force-password-new"
        />
        <AppInput
          label={t('security.repeatPassword')}
          value={repeat}
          onChangeText={setRepeat}
          secure
          error={mismatch ? t('security.mismatch') : undefined}
          showPasswordLabel={t('a11y.showPassword')}
          hidePasswordLabel={t('a11y.hidePassword')}
          testID="force-password-repeat"
        />
        {submit.isError ? (
          <Banner
            tone="error"
            title={
              submit.error instanceof ApiError
                ? submit.error.message
                : t('errors.generic')
            }
          />
        ) : null}
        <AppButton
          title={t('forcePassword.submit')}
          disabled={!canSubmit}
          loading={submit.isPending}
          onPress={() => submit.mutate()}
          testID="force-password-save"
        />
      </Card>

      <View style={styles.foot}>
        <AppButton
          tone="ghost"
          title={t('profile.signOut')}
          onPress={() => void signOut()}
          testID="force-password-signout"
        />
      </View>
    </Screen>
  );
}
